import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Clear from "@mui/icons-material/Clear";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { SxProps, useTheme } from "@mui/material/styles";

import { clone, reverse } from "lodash";

import { useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import { SHOW_INACTIVE_PATIENTS_SEARCH } from "constants/feature-flags";
import {
  selectCurrentClinic,
  selectCurrentPractice
} from "features/auth/auth-slice";
import { useDebounce, useRecentlySearchedPatients } from "hooks/";
import { useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";
import { getFullName } from "utils/common";

import PatientSearchOption from "./patient-search-option.component";

export const PATIENTS_SEARCH_QUERY =
  "{id,sex,first_name,middle_name,preferred_name,last_name,birthdate,age_years,phone,phone_type,secondary_phone,email,secondary_email,photo,is_active,primary_contact_notes,secondary_contact_notes,crm_segment,box_folder_id,city,state,preferred_clinic{id,name,region{id,name}},preferred_provider{full_name}}";

type PatientSearchProps = {
  onPatientSelected?: (patient?: GroupedPatient) => void;
  placeholder?: string;
  minWidth?: number;
  disableClearable?: boolean;
  showRecent?: boolean;
  showLengthLimitHint?: boolean;
  clinicId?: number;
  sx?: SxProps;
};

type GroupKey = "recent" | "search";

interface GroupedPatient extends Patient {
  group_key: GroupKey;
}

export default function PatientSearch({
  onPatientSelected,
  minWidth = 375,
  showLengthLimitHint,
  placeholder = "Search patients by name, phone number, email, id",
  disableClearable = false,
  showRecent = false,
  clinicId,
  sx
}: PatientSearchProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const flags = useFlags();
  const showInactivePatients = flags[SHOW_INACTIVE_PATIENTS_SEARCH];
  const currentPractice = useAppSelector(selectCurrentPractice);
  const currentClinic = useAppSelector(selectCurrentClinic);
  const effectiveClinicId = currentClinic?.id ?? clinicId;
  const [search, setSearch] = useState<string>("");
  const [selectedPatient, setSelectedPatient] = useState<GroupedPatient | null>(
    null
  );
  const [open, setOpen] = useState<boolean>(false);
  const [focus, setFocus] = useState<boolean>(false);
  const { value: dSearch, isDebouncing } = useDebounce(search, 200);
  const { addPatientToRecent, recentPatientIds } =
    useRecentlySearchedPatients();
  const [patients, setPatients] = useState<GroupedPatient[]>([]);
  const {
    data: searchPatients,
    isFetching: isFetchingForSearch,
    isLoading: isLoadingSearch,
    isUninitialized,
    refetch
  } = useQueryPatientsQuery(
    {
      search: dSearch,
      baseFilter: {
        preferred_clinic: Boolean(currentPractice?.limit_to_current_clinic)
          ? effectiveClinicId
          : undefined
      },
      is_active: showInactivePatients ? undefined : true,
      query: PATIENTS_SEARCH_QUERY
    },
    {
      skip:
        !open ||
        !currentPractice ||
        !effectiveClinicId ||
        !dSearch ||
        dSearch.length < 3,
      refetchOnMountOrArgChange: true
    }
  );

  const { data: recentPatients, isFetching: isFetchingForRecent } =
    useQueryPatientsQuery(
      {
        search: dSearch.length < 3 ? "" : dSearch,
        ids: recentPatientIds,
        baseFilter: {
          preferred_clinic: Boolean(currentPractice?.limit_to_current_clinic)
            ? currentClinic?.id
            : undefined
        },
        is_active: showInactivePatients ? undefined : true,
        query: PATIENTS_SEARCH_QUERY
      },
      {
        skip:
          !open ||
          !currentPractice ||
          !effectiveClinicId ||
          !showRecent ||
          recentPatientIds?.length === 0
      }
    );
  const [isFetching, setIsFetching] = useState(
    isFetchingForSearch || isFetchingForRecent
  );

  useEffect(() => {
    setIsFetching(isFetchingForSearch || isFetchingForRecent);
  }, [isFetchingForSearch, isFetchingForRecent]);

  useEffect(() => {
    if (
      open &&
      dSearch.length >= 3 &&
      !isFetchingForSearch &&
      !isLoadingSearch
    ) {
      setPatients([
        ...(searchPatients
          ? searchPatients.map((patient) => ({
              ...patient,
              group_key: "search" as GroupKey
            }))
          : [])
      ]);
    } else if (open && (showRecent || patients.length > 0)) {
      const reversedRecentPatientIds = reverse(clone(recentPatientIds));
      setPatients(
        reversedRecentPatientIds
          .map((id: number) => {
            const patient = recentPatients?.find(
              (patient) => patient.id === id
            );
            return patient
              ? {
                  ...patient,
                  group_key: "recent"
                }
              : undefined;
          })
          .filter(Boolean)
      );
    }
  }, [
    searchPatients,
    recentPatientIds,
    recentPatients,
    search,
    patients.length,
    showRecent,
    isFetchingForSearch,
    isLoadingSearch,
    open,
    dSearch.length
  ]);

  useEffect(() => {
    if (showRecent && selectedPatient) {
      addPatientToRecent(selectedPatient.id);
    }
  }, [search, selectedPatient, addPatientToRecent, focus, showRecent]);

  const showRecentItems =
    showRecent &&
    (dSearch.length < 3 || isFetchingForSearch || isLoadingSearch);

  return (
    <Box
      sx={{
        boxSizing: "border-box",
        boxShadow: theme.shadows[0],
        borderRadius: "28px",
        pl: 1,
        display: "flex",
        alignItems: "center",
        minWidth: theme.typography.pxToRem(minWidth),
        width: "100%",
        border: "solid 1px #054743",
        ...sx
      }}
    >
      <Autocomplete
        data-testid="top-nav-search"
        openOnFocus
        autoHighlight={!showRecentItems}
        open={open}
        autoComplete={false}
        clearOnBlur={false}
        blurOnSelect
        fullWidth
        popupIcon={<ExpandMoreOutlinedIcon />}
        options={patients}
        filterOptions={(options) => options}
        disableClearable={disableClearable}
        forcePopupIcon={false}
        loading={isFetching || isDebouncing}
        inputValue={search}
        value={selectedPatient}
        onFocus={() => {
          if (!isUninitialized) {
            refetch();
          }
          setTimeout(() => {
            setPatients([]);
            setOpen(true);
            setFocus(true);
          }, 1);
        }}
        onBlur={() => {
          setPatients([]);
          setOpen(false);
          setFocus(false);
        }}
        noOptionsText="No results"
        onInputChange={(_, value, reason) => {
          if (reason === "reset") {
            setSearch("");
            setSelectedPatient(null);
          } else {
            setSearch(value);
          }
        }}
        onChange={(_, value) => {
          setSelectedPatient(value || null);
          if (onPatientSelected) {
            onPatientSelected(value || undefined);
          } else if (value) {
            navigate(`patients/${value.id}/`);
          }
          setOpen(false);
          setFocus(false);
        }}
        getOptionLabel={(option: GroupedPatient) =>
          getFullName({
            firstName: option?.first_name,
            middleName: option?.middle_name,
            preferredName: option?.preferred_name,
            lastName: option?.last_name
          })
        }
        renderInput={({ InputLabelProps, InputProps, ...params }) => (
          <InputBase
            {...InputProps}
            {...params}
            sx={{ pr: 0 }}
            placeholder={placeholder}
            startAdornment={<SearchIcon sx={{ mr: 0.25 }} color="action" />}
            endAdornment={
              <Stack
                direction="row"
                columnGap={0.5}
                sx={{ height: "40px", alignItems: "center" }}
              >
                {showLengthLimitHint &&
                  open &&
                  search.length > 0 &&
                  search.length < 3 && (
                    <Typography sx={{ color: neutral[600], mr: 1 }}>
                      Please enter at least 3 characters
                    </Typography>
                  )}

                {(isFetching || isDebouncing) && dSearch.length >= 3 && (
                  <CircularProgress
                    color="primary"
                    size="small"
                    sx={{
                      width: "25px",
                      height: "25px"
                    }}
                  />
                )}
                <IconButton
                  sx={{
                    visibility: search.length > 0 ? "visible" : "hidden",
                    mr: 0.25
                  }}
                  onClick={() => {
                    setSearch("");
                    setSelectedPatient(null);
                    setOpen(false);
                    setFocus(false);
                    if (onPatientSelected) {
                      onPatientSelected(undefined);
                    }
                  }}
                  size="small"
                  edge="end"
                >
                  <Clear color="primary" />
                </IconButton>
              </Stack>
            }
          />
        )}
        slotProps={{
          popper: {
            sx: {
              zIndex: (theme) => theme.zIndex.snackbar + 1
            }
          }
        }}
        groupBy={
          showRecentItems
            ? (option: GroupedPatient) =>
                option.group_key === "recent" ? "Recently viewed" : ""
            : undefined
        }
        renderOption={(props, option: GroupedPatient) => (
          <PatientSearchOption
            renderProps={props}
            option={option}
            key={option.id}
            showPatientTags={!isDebouncing && !isFetchingForSearch}
          />
        )}
      />
    </Box>
  );
}
